import React, { useMemo, useState } from "react";
import {
  INK,
  MUTED,
  GRID,
  SURFACE,
  TRAY,
  TIER_RAMP,
  MASTERY_BANDS,
} from "../tokens";
import { SCHOOL_MIN_LEARNERS } from "../model";
import { relBreakdown, relGlyph } from "../format";
import { TableCard, TableNote, Legend, PlotNote } from "../ui";

// Units x schools matrix for one grade cohort: the scan view that answers "which
// units do our schools diverge on?", where the list view's expansion answers
// "how do schools differ on THIS unit". Rows are the same filtered/ranked units
// the list shows; columns are the district's schools in one fixed order.
//
// A cell can only carry one number, so the three-band composition collapses to a
// single measure here (that is the trade for scanning across a row) and the exact
// counts live in the cell tooltip -- a number in every one of ~500 cells is noise,
// and the list view is the table view for anyone who wants the figures.

// Absolute measure: one hue, light->dark, reusing the dashboard's existing green
// sequential ramp rather than introducing a second green. The palest step is
// dropped (it can't clear 2:1 against a white surface, so a cell wouldn't read as
// a cell) which leaves four bands -- validated: monotone L, ΔL gaps >= 0.06,
// light end 2.12:1, single hue.
const ABS_RAMP = TIER_RAMP.slice(1, 5); // #74c476 #41ab5d #238b45 #005a32
const ABS_BANDS = [70, 85, 95]; // upper edges; index i => ABS_RAMP[i]
const ABS_LABELS = ["< 70%", "70–84%", "85–94%", "95–100%"];

// Relative measure: diverging, two poles + a neutral middle (never a hue at the
// midpoint). Blue = ahead of the district on this unit, red = behind, gray =
// within the deadband. Each arm validated as its own one-hue ramp; the two inner
// steps separate at CVD ΔE 11.9 / normal-vision ΔE 17.7.
const DELTA_NEUTRAL = "#f0efec";
const DELTA_SCALE = [
  [-20, "#c93231", "20+ pts behind"],
  [-8, "#ef9c9b", "8–20 behind"],
  [8, DELTA_NEUTRAL, "within 8 pts"],
  [20, "#86b6ef", "8–20 ahead"],
  [Infinity, "#256abf", "20+ pts ahead"],
];

const NO_DATA = "#fbfbf9";

const reached = (r) =>
  r && r.total
    ? (100 * (r.mastered_outright + r.eventually_mastered)) / r.total
    : null;

const absColor = (v) => {
  const i = ABS_BANDS.findIndex((edge) => v < edge);
  return ABS_RAMP[i === -1 ? ABS_RAMP.length - 1 : i];
};
const deltaColor = (d) => DELTA_SCALE.find(([edge]) => d < edge)[1];

// Highest-minus-lowest school rate on one unit, in points. Deliberately ignores
// sub-threshold schools -- a 2-learner cell at 0% would otherwise drive the whole
// ranking -- and needs two qualifying schools to exist at all. Exported because
// the tab sorts by it while the matrix displays it; one definition, two callers.
const spreadOf = (rows) => {
  const solid = (rows || []).filter(
    (c) => c && c.total && c.learners >= SCHOOL_MIN_LEARNERS
  );
  const pcts = solid.map(reached);
  return {
    counted: solid.length,
    spread:
      pcts.length >= 2
        ? Math.round(Math.max(...pcts) - Math.min(...pcts))
        : null,
  };
};

const COL_W = 27;
const CELL_H = 22;
const UNIT_W = 236;
const NUM_W = 54;

// Sticky so the unit name stays put while the schools scroll horizontally.
const stickyCol = (extra) => ({
  position: "sticky",
  left: 0,
  zIndex: 2,
  background: "inherit",
  ...extra,
});

const HeadCell = ({ label, active }) => (
  <div
    style={{
      width: COL_W,
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-end",
      height: 118,
    }}
    title={label}
  >
    <span
      style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)",
        fontSize: 11,
        color: active ? INK : MUTED,
        fontWeight: active ? 700 : 500,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxHeight: 114,
      }}
    >
      {label}
    </span>
  </div>
);

const Cell = ({ row, districtPct, metric, onHover, active }) => {
  const pctVal = reached(row);
  const thin = row && row.learners < SCHOOL_MIN_LEARNERS;
  let bg = NO_DATA;
  if (pctVal != null)
    bg =
      metric === "delta"
        ? deltaColor(pctVal - districtPct)
        : absColor(pctVal);
  const title =
    pctVal == null
      ? `${row ? row.group : "No data"} — no mastery activity on this unit`
      : [
          row.group,
          `${Math.round(pctVal)}% reached mastery` +
            (metric === "delta"
              ? ` (${pctVal - districtPct >= 0 ? "+" : ""}${Math.round(
                  pctVal - districtPct
                )} pts vs district)`
              : ""),
          MASTERY_BANDS.map((b) => `${row[b.key] || 0} ${b.short}`).join(" · "),
          `${row.total} events · ${row.learners} learners` +
            (thin ? ` — thin, under ${SCHOOL_MIN_LEARNERS}` : ""),
        ].join("\n");
  return (
    <div
      style={{ width: COL_W, display: "flex", justifyContent: "center" }}
      onMouseEnter={onHover}
    >
      <div
        title={title}
        style={{
          width: COL_W - 3,
          height: CELL_H - 2,
          borderRadius: 3,
          background: bg,
          opacity: thin ? 0.35 : 1,
          border: pctVal == null ? `1px dashed ${GRID}` : "none",
          outline: active ? `1px solid ${INK}` : "none",
        }}
      />
    </div>
  );
};

const UnitSchoolMatrix = ({
  units,
  groups,
  byGroup,
  metric,
  loading,
  error,
  gradeText,
}) => {
  const [hover, setHover] = useState({});

  // One row per unit: its cells in fixed column order, plus its spread. Columns
  // with nothing in them anywhere are dropped -- in a real district a good half
  // of the grid is empty, and a school that never touched any of these units is
  // a column of dashes that only makes the rest harder to scan. The count is
  // reported rather than silently swallowed.
  const { rows, cols, emptyCols } = useMemo(() => {
    const byUnit = units.map((u) => {
      const unitRows = byGroup[String(u.unit_id)] || [];
      const byId = {};
      unitRows.forEach((r) => {
        byId[String(r.group_id)] = r;
      });
      return { u, byId, ...spreadOf(unitRows) };
    });
    const live = groups.filter((g) =>
      byUnit.some((r) => {
        const c = r.byId[String(g.group_id)];
        return c && c.total;
      })
    );
    return {
      cols: live,
      emptyCols: groups.length - live.length,
      rows: byUnit.map((r) => ({
        unit: r.u,
        districtPct: reached(r.u) || 0,
        cells: live.map((g) => r.byId[String(g.group_id)] || null),
        counted: r.counted,
        spread: r.spread,
      })),
    };
  }, [units, groups, byGroup]);

  if (loading)
    return <TableNote>Loading the school comparison…</TableNote>;
  if (error)
    return <TableNote>Couldn’t load the school comparison.</TableNote>;
  if (!cols.length)
    return (
      <TableNote>
        No school in this district has {gradeText} mastery activity on these
        units in this window.
      </TableNote>
    );

  const legendItems =
    metric === "delta"
      ? DELTA_SCALE.map(([, color, label]) => ({ color, label }))
      : ABS_RAMP.map((color, i) => ({ color, label: ABS_LABELS[i] }));

  return (
    <>
      <Legend
        items={legendItems}
        trailing={
          <>
            {rows.length} units × {cols.length} schools
            {emptyCols > 0 &&
              ` (${emptyCols} with no activity hidden)`}{" "}
            ·{" "}
            {metric === "delta"
              ? "each cell vs its own unit’s district rate"
              : "share of mastery events reached"}{" "}
            · faded = under {SCHOOL_MIN_LEARNERS} learners · dashed = no activity
          </>
        }
      />
      <TableCard>
        <div style={{ overflowX: "auto" }}>
          <div style={{ display: "inline-block", minWidth: "100%" }}>
            {/* header band */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                background: TRAY,
                padding: "6px 8px 4px",
              }}
            >
              <div
                style={stickyCol({
                  width: UNIT_W,
                  background: TRAY,
                  fontSize: 11,
                  letterSpacing: ".05em",
                  textTransform: "uppercase",
                  color: MUTED,
                  fontWeight: 600,
                  alignSelf: "flex-end",
                })}
              >
                Unit
              </div>
              <div
                style={{
                  width: NUM_W,
                  textAlign: "right",
                  paddingRight: 10,
                  fontSize: 11,
                  color: MUTED,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: ".05em",
                }}
              >
                Dist.
              </div>
              {cols.map((g, c) => (
                <HeadCell
                  key={g.group_id}
                  label={g.group}
                  active={hover.c === c}
                />
              ))}
              <div
                style={{
                  width: NUM_W,
                  textAlign: "right",
                  fontSize: 11,
                  color: MUTED,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: ".05em",
                }}
                title="Highest minus lowest school rate, over schools that clear the learner bar"
              >
                Spread
              </div>
            </div>
            {/* rows */}
            {rows.map((r, ri) => (
              <div
                key={r.unit.unit_id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0 8px",
                  background: hover.r === ri ? TRAY : SURFACE,
                  borderTop: `1px solid ${GRID}`,
                }}
                onMouseLeave={() => setHover({})}
              >
                <div
                  style={stickyCol({
                    width: UNIT_W,
                    background: hover.r === ri ? TRAY : SURFACE,
                    fontSize: 12.5,
                    color: INK,
                    fontWeight: hover.r === ri ? 650 : 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    paddingRight: 8,
                    display: "flex",
                    alignItems: "baseline",
                    gap: 6,
                  })}
                  title={`${r.unit.unit} · ${r.unit.subject} · level ${
                    r.unit.level == null ? "?" : r.unit.level
                  } · ${r.unit.learners} learners district-wide${
                    relBreakdown(r.unit.by_level)
                      ? ` · ${relBreakdown(r.unit.by_level)}`
                      : ""
                  }`}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.unit.unit}
                  </span>
                  {/* the same unit name exists at several course levels within a
                      standardset, so without the level two rows read as
                      duplicates -- the list carries the same chip.
                      Grade-relative direction rides along as the glyph alone:
                      this column is sticky and already truncating the unit
                      name, so the words go in the row title instead. */}
                  <span
                    style={{ fontSize: 10.5, color: MUTED, flex: "0 0 auto" }}
                  >
                    Lv{" "}
                    {r.unit.level == null
                      ? "?"
                      : r.unit.level === 0
                      ? "K"
                      : r.unit.level <= -1
                      ? "PreK"
                      : r.unit.level}
                    {relGlyph(r.unit.by_level)}
                  </span>
                </div>
                <div
                  style={{
                    width: NUM_W,
                    textAlign: "right",
                    paddingRight: 10,
                    fontSize: 12,
                    color: INK,
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                  }}
                  title={`District: ${r.unit.total} events · ${r.unit.learners} learners`}
                >
                  {Math.round(r.districtPct)}%
                </div>
                {r.cells.map((cell, ci) => (
                  <Cell
                    key={cols[ci].group_id}
                    row={cell}
                    districtPct={r.districtPct}
                    metric={metric}
                    active={hover.r === ri && hover.c === ci}
                    onHover={() => setHover({ r: ri, c: ci })}
                  />
                ))}
                <div
                  style={{
                    width: NUM_W,
                    textAlign: "right",
                    fontSize: 12,
                    color: r.spread == null ? MUTED : INK,
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                  }}
                  title={
                    r.spread == null
                      ? `Fewer than two schools clear ${SCHOOL_MIN_LEARNERS} learners on this unit`
                      : `${r.spread} points between the best and worst of ${r.counted} schools with enough learners`
                  }
                >
                  {r.spread == null ? "–" : `${r.spread}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </TableCard>
      <PlotNote
        label="How to read this grid"
        details={
          <>
            Sort by <b>Spread</b> to rank units by how much the schools disagree
            — that is the inconsistent-practice signal, and it is computed only
            over schools clearing {SCHOOL_MIN_LEARNERS} learners (faded cells are
            shown but not counted, and a unit with fewer than two such schools
            has no spread). Hover a cell for its counts. A school with no
            activity on a unit is dashed, not zero — “didn’t teach it here” and
            “taught it and missed” are different findings. Switch to the list
            view for the full three-band composition and the per-standard detail.
          </>
        }
      >
        Each cell is one school’s {gradeText} learners on one unit,{" "}
        {metric === "delta"
          ? "colored by distance from that unit’s district rate — a row reads as who is ahead and who is behind."
          : "colored by the share of its mastery events that reached mastery."}
      </PlotNote>
    </>
  );
};

export { UnitSchoolMatrix, spreadOf };
